import React from 'react';

type IconProps = {
  className?: string;
};

/**
 * Shared visual language across the set:
 * - Every icon sits on an extruded "chip" (two offset rounded squares) so it reads as a
 *   physical, lifted object rather than a flat line glyph.
 * - Neutral surfaces use a cool metallic gradient (light top face -> dark side face).
 * - The accent color (#F5401F) is always rendered with its own warm gradient + a small
 *   glossy highlight ellipse so it reads as a glass/lacquer bead, not a flat fill.
 * - A soft drop shadow (feDropShadow) is applied to the whole icon group to lift it off
 *   the page.
 * - Every gradient/filter id is namespaced per-icon to avoid ID collisions when multiple
 *   icons are mounted on the same page.
 */

export const Icons = {
  AiDrivenLearning: ({ className }: IconProps) => (
    <svg viewBox="0 0 120 120" className={className} xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id="ai-top" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#8a8a8a" />
          <stop offset="1" stopColor="#4d4d4d" />
        </linearGradient>
        <linearGradient id="ai-side" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#3a3a3a" />
          <stop offset="1" stopColor="#232323" />
        </linearGradient>
        <linearGradient id="ai-accent" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#ff7a54" />
          <stop offset="1" stopColor="#d9300c" />
        </linearGradient>
        <radialGradient id="ai-gloss" cx="0.32" cy="0.28" r="0.75">
          <stop offset="0" stopColor="#ffffff" stopOpacity="0.85" />
          <stop offset="1" stopColor="#ffffff" stopOpacity="0" />
        </radialGradient>
        <filter id="ai-shadow" x="-30%" y="-30%" width="160%" height="160%">
          <feDropShadow dx="0" dy="4" stdDeviation="4" floodColor="#000" floodOpacity="0.28" />
        </filter>
      </defs>

      <g filter="url(#ai-shadow)">
        {/* extruded chip body */}
        <rect x="34" y="38" width="46" height="46" rx="9" fill="url(#ai-side)" />
        <rect x="32" y="34" width="46" height="46" rx="9" fill="url(#ai-top)" />
        <rect x="32" y="34" width="46" height="20" rx="9" fill="#ffffff" opacity="0.08" />

        {/* circuit legs, each capped with a shaded bead */}
        <g stroke="#4d4d4d" strokeWidth="3.4" strokeLinecap="round">
          <line x1="42" y1="34" x2="42" y2="22" />
          <line x1="55" y1="34" x2="55" y2="18" />
          <line x1="68" y1="34" x2="68" y2="22" />
          <line x1="42" y1="80" x2="42" y2="98" />
          <line x1="55" y1="80" x2="55" y2="102" />
          <line x1="68" y1="80" x2="68" y2="98" />
          <line x1="32" y1="44" x2="18" y2="44" />
          <line x1="32" y1="57" x2="14" y2="57" />
          <line x1="32" y1="70" x2="18" y2="70" />
          <line x1="78" y1="44" x2="92" y2="44" />
          <line x1="78" y1="57" x2="96" y2="57" />
          <line x1="78" y1="70" x2="92" y2="70" />
        </g>
        <g fill="url(#ai-top)" stroke="#2b2b2b" strokeWidth="1">
          <circle cx="42" cy="20" r="3.4" />
          <circle cx="55" cy="16" r="3.4" />
          <circle cx="68" cy="20" r="3.4" />
          <circle cx="42" cy="100" r="3.4" />
          <circle cx="55" cy="104" r="3.4" />
          <circle cx="68" cy="100" r="3.4" />
          <circle cx="16" cy="44" r="3.4" />
          <circle cx="12" cy="57" r="3.4" />
          <circle cx="16" cy="70" r="3.4" />
          <circle cx="94" cy="44" r="3.4" />
          <circle cx="98" cy="57" r="3.4" />
          <circle cx="94" cy="70" r="3.4" />
        </g>

        {/* faceted "spark" core, extruded */}
        <polygon points="56,49 71,57 56,65 41,57" fill="#7a1a06" opacity="0.5" />
        <polygon points="56,46 71,54 56,62 41,54" fill="url(#ai-accent)" />
        <polygon points="56,46 71,54 56,58 41,54" fill="url(#ai-gloss)" />
        <rect x="49" y="59" width="14" height="7" rx="1.5" fill="url(#ai-accent)" />
        <line x1="66" y1="57" x2="71" y2="70" stroke="url(#ai-accent)" strokeWidth="2.6" strokeLinecap="round" />
        <circle cx="71" cy="70" r="2.4" fill="url(#ai-accent)" />
      </g>
    </svg>
  ),

  AutomatedWorkflows: ({ className }: IconProps) => (
    <svg viewBox="0 0 120 120" className={className} xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id="wf-top" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#9a9a9a" />
          <stop offset="1" stopColor="#525252" />
        </linearGradient>
        <linearGradient id="wf-side" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#3a3a3a" />
          <stop offset="1" stopColor="#202020" />
        </linearGradient>
        <linearGradient id="wf-accent" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#ff7a54" />
          <stop offset="1" stopColor="#d9300c" />
        </linearGradient>
        <radialGradient id="wf-gloss" cx="0.35" cy="0.3" r="0.7">
          <stop offset="0" stopColor="#ffffff" stopOpacity="0.8" />
          <stop offset="1" stopColor="#ffffff" stopOpacity="0" />
        </radialGradient>
        <filter id="wf-shadow" x="-30%" y="-30%" width="160%" height="160%">
          <feDropShadow dx="0" dy="4" stdDeviation="4" floodColor="#000" floodOpacity="0.28" />
        </filter>
      </defs>

      <g filter="url(#wf-shadow)">
        {/* extruded gear hub */}
        <g transform="translate(0,3)">
          <path
            d="M55 32 l7 0 2 9 7 3 8-5 5 5-5 8 3 7 9 2 0 7 -9 2 -3 7 5 8-5 5-8-5-7 3-2 9-7 0-2-9-7-3-8 5-5-5 5-8-3-7-9-2 0-7 9-2 3-7-5-8 5-5 8 5 7-3z"
            fill="url(#wf-side)"
          />
        </g>
        <path
          d="M55 29 l7 0 2 9 7 3 8-5 5 5-5 8 3 7 9 2 0 7 -9 2 -3 7 5 8-5 5-8-5-7 3-2 9-7 0-2-9-7-3-8 5-5-5 5-8-3-7-9-2 0-7 9-2 3-7-5-8 5-5 8 5 7-3z"
          fill="url(#wf-top)"
        />
        <circle cx="58.5" cy="58.5" r="12" fill="url(#wf-side)" />
        <circle cx="58" cy="58" r="12" fill="#2b2b2b" />
        <circle cx="58" cy="58" r="7" fill="url(#wf-top)" />
        <path d="M46 46 a17 17 0 0 1 24 -3" fill="none" stroke="#ffffff" strokeOpacity="0.18" strokeWidth="4" strokeLinecap="round" />

        {/* orbiting refresh accent, extruded ring */}
        <path d="M92 27 A13 13 0 1 1 87 24" fill="none" stroke="#7a1a06" strokeWidth="6.5" strokeLinecap="round" opacity="0.4" />
        <path d="M92 25 A13 13 0 1 1 87 22" fill="none" stroke="url(#wf-accent)" strokeWidth="6" strokeLinecap="round" />
        <polygon points="87,16 87,27 97,22" fill="url(#wf-accent)" />
        <circle cx="90" cy="22" r="9" fill="url(#wf-gloss)" />
      </g>
    </svg>
  ),

  SkillsCompetencyManagement: ({ className }: IconProps) => (
    <svg viewBox="0 0 120 120" className={className} xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id="sk-top" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#9a9a9a" />
          <stop offset="1" stopColor="#4d4d4d" />
        </linearGradient>
        <linearGradient id="sk-side" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#3a3a3a" />
          <stop offset="1" stopColor="#1f1f1f" />
        </linearGradient>
        <linearGradient id="sk-ribbon" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#6a6a6a" />
          <stop offset="1" stopColor="#3d3d3d" />
        </linearGradient>
        <linearGradient id="sk-accent" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#ff8a63" />
          <stop offset="1" stopColor="#d9300c" />
        </linearGradient>
        <radialGradient id="sk-gloss" cx="0.35" cy="0.3" r="0.75">
          <stop offset="0" stopColor="#ffffff" stopOpacity="0.85" />
          <stop offset="1" stopColor="#ffffff" stopOpacity="0" />
        </radialGradient>
        <filter id="sk-shadow" x="-30%" y="-30%" width="160%" height="160%">
          <feDropShadow dx="0" dy="4" stdDeviation="4" floodColor="#000" floodOpacity="0.28" />
        </filter>
      </defs>

      <g filter="url(#sk-shadow)">
        {/* ribbon tails, extruded */}
        <polygon points="47,68 41,96 51,89 57,96" fill="url(#sk-side)" />
        <polygon points="73,68 79,96 69,89 63,96" fill="url(#sk-side)" />
        <polygon points="46,66 40,94 50,87 56,94" fill="url(#sk-ribbon)" />
        <polygon points="74,66 80,94 70,87 64,94" fill="url(#sk-ribbon)" />

        {/* medallion body, extruded disc */}
        <circle cx="60" cy="52" r="25" fill="url(#sk-side)" />
        <circle cx="60" cy="50" r="25" fill="url(#sk-top)" />
        <circle cx="60" cy="50" r="18" fill="none" stroke="#232323" strokeWidth="2.2" opacity="0.35" />
        <path d="M46 39 a20 20 0 0 1 27 -5" fill="none" stroke="#ffffff" strokeOpacity="0.2" strokeWidth="5" strokeLinecap="round" />

        {/* faceted star, extruded */}
        <polygon
          points="60,37 64,47 75,47.5 66,54 69,65 60,58 51,65 54,54 45,47.5 56,47"
          fill="#7a1a06"
          opacity="0.4"
          transform="translate(0,3)"
        />
        <polygon
          points="60,37 64,47 75,47.5 66,54 69,65 60,58 51,65 54,54 45,47.5 56,47"
          fill="url(#sk-accent)"
        />
        <circle cx="55" cy="46" r="7" fill="url(#sk-gloss)" />
      </g>
    </svg>
  ),

  MultiTenantSupport: ({ className }: IconProps) => (
    <svg viewBox="0 0 120 120" className={className} xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id="mt-topA" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#8a8a8a" />
          <stop offset="1" stopColor="#4d4d4d" />
        </linearGradient>
        <linearGradient id="mt-topB" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#a3a3a3" />
          <stop offset="1" stopColor="#5c5c5c" />
        </linearGradient>
        <linearGradient id="mt-side" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#3a3a3a" />
          <stop offset="1" stopColor="#1f1f1f" />
        </linearGradient>
        <linearGradient id="mt-accent" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#ff7a54" />
          <stop offset="1" stopColor="#d9300c" />
        </linearGradient>
        <filter id="mt-shadow" x="-30%" y="-30%" width="160%" height="160%">
          <feDropShadow dx="0" dy="4" stdDeviation="4" floodColor="#000" floodOpacity="0.28" />
        </filter>
      </defs>

      <g filter="url(#mt-shadow)">
        {/* three extruded towers, each with a lit top face + darker front face */}
        <g>
          <rect x="20" y="66" width="20" height="8" fill="url(#mt-side)" />
          <rect x="20" y="58" width="20" height="34" rx="2" fill="url(#mt-topA)" />
          <g stroke="#2b2b2b" strokeWidth="2" strokeLinecap="round" opacity="0.55">
            <line x1="25" y1="66" x2="30" y2="66" />
            <line x1="25" y1="76" x2="30" y2="76" />
            <line x1="25" y1="84" x2="30" y2="84" />
          </g>
        </g>
        <g>
          <rect x="46" y="44" width="24" height="8" fill="url(#mt-side)" />
          <rect x="46" y="36" width="24" height="56" rx="2" fill="url(#mt-topB)" />
          <rect x="46" y="36" width="24" height="14" rx="2" fill="#ffffff" opacity="0.1" />
          <g stroke="#2b2b2b" strokeWidth="2" strokeLinecap="round" opacity="0.55">
            <line x1="52" y1="46" x2="58" y2="46" />
            <line x1="52" y1="56" x2="58" y2="56" />
            <line x1="52" y1="66" x2="58" y2="66" />
            <line x1="52" y1="76" x2="58" y2="76" />
          </g>
        </g>
        <g>
          <rect x="80" y="69" width="20" height="8" fill="url(#mt-side)" />
          <rect x="80" y="61" width="20" height="31" rx="2" fill="url(#mt-topA)" />
          <g stroke="#2b2b2b" strokeWidth="2" strokeLinecap="round" opacity="0.55">
            <line x1="85" y1="69" x2="90" y2="69" />
            <line x1="85" y1="79" x2="90" y2="79" />
          </g>
        </g>

        {/* network beads connecting the tenants, lifted above the skyline */}
        <g stroke="url(#mt-accent)" strokeWidth="3" fill="none" strokeLinecap="round">
          <line x1="30" y1="48" x2="58" y2="28" />
          <line x1="58" y1="28" x2="90" y2="53" />
        </g>
        <g fill="url(#mt-accent)">
          <circle cx="30" cy="48" r="5" />
          <circle cx="58" cy="26" r="6" />
          <circle cx="90" cy="53" r="5" />
        </g>
        <circle cx="56" cy="23" r="3" fill="#ffffff" opacity="0.6" />
      </g>
    </svg>
  ),

  HrmsIntegration: ({ className }: IconProps) => (
    <svg viewBox="0 0 120 120" className={className} xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id="hr-top" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#9a9a9a" />
          <stop offset="1" stopColor="#4d4d4d" />
        </linearGradient>
        <linearGradient id="hr-side" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#3a3a3a" />
          <stop offset="1" stopColor="#1f1f1f" />
        </linearGradient>
        <linearGradient id="hr-accent" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#ff7a54" />
          <stop offset="1" stopColor="#d9300c" />
        </linearGradient>
        <radialGradient id="hr-gloss" cx="0.32" cy="0.28" r="0.8">
          <stop offset="0" stopColor="#ffffff" stopOpacity="0.85" />
          <stop offset="1" stopColor="#ffffff" stopOpacity="0" />
        </radialGradient>
        <filter id="hr-shadow" x="-30%" y="-30%" width="160%" height="160%">
          <feDropShadow dx="0" dy="4" stdDeviation="4" floodColor="#000" floodOpacity="0.28" />
        </filter>
      </defs>

      <g filter="url(#hr-shadow)">
        {/* extruded card backplate */}
        <rect x="16" y="26" width="66" height="70" rx="10" fill="url(#hr-side)" />
        <rect x="14" y="22" width="66" height="70" rx="10" fill="url(#hr-top)" />
        <rect x="14" y="22" width="66" height="24" rx="10" fill="#ffffff" opacity="0.08" />

        {/* head, shaded sphere */}
        <circle cx="47" cy="46" r="13" fill="#232323" opacity="0.5" transform="translate(0,2)" />
        <circle cx="47" cy="46" r="13" fill="url(#hr-top)" />
        <ellipse cx="43" cy="41" rx="6" ry="4.5" fill="#ffffff" opacity="0.22" />

        {/* torso */}
        <path d="M27 88 C27 66 35 58 47 58 C59 58 67 66 67 88 Z" fill="url(#hr-side)" />
        <path d="M27 86 C27 64 35 56 47 56 C59 56 67 64 67 86 Z" fill="url(#hr-top)" />

        {/* extruded plug / integration accent */}
        <rect x="70" y="46" width="17" height="9" rx="2" fill="url(#hr-accent)" />
        <rect x="76" y="55" width="6" height="12" fill="#d9300c" />
        <rect x="76" y="55" width="6" height="12" fill="url(#hr-accent)" opacity="0.9" />
        <rect x="76" y="67" width="6" height="10" rx="3" fill="url(#hr-accent)" />
        <circle cx="79" cy="50" r="7" fill="url(#hr-gloss)" />
      </g>
    </svg>
  ),

  RealtimeAnalytics: ({ className }: IconProps) => (
    <svg viewBox="0 0 120 120" className={className} xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id="ra-top" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#9a9a9a" />
          <stop offset="1" stopColor="#4d4d4d" />
        </linearGradient>
        <linearGradient id="ra-side" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#3a3a3a" />
          <stop offset="1" stopColor="#1f1f1f" />
        </linearGradient>
        <linearGradient id="ra-accent" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#ff8a63" />
          <stop offset="1" stopColor="#d9300c" />
        </linearGradient>
        <radialGradient id="ra-gloss" cx="0.35" cy="0.3" r="0.75">
          <stop offset="0" stopColor="#ffffff" stopOpacity="0.8" />
          <stop offset="1" stopColor="#ffffff" stopOpacity="0" />
        </radialGradient>
        <filter id="ra-shadow" x="-30%" y="-30%" width="160%" height="160%">
          <feDropShadow dx="0" dy="4" stdDeviation="4" floodColor="#000" floodOpacity="0.28" />
        </filter>
      </defs>

      <g filter="url(#ra-shadow)">
        {/* extruded dashboard panel */}
        <rect x="17" y="30" width="64" height="46" rx="7" fill="url(#ra-side)" />
        <rect x="15" y="26" width="64" height="46" rx="7" fill="url(#ra-top)" />
        <rect x="15" y="26" width="64" height="16" rx="7" fill="#ffffff" opacity="0.08" />
        <rect x="42" y="72" width="10" height="8" fill="url(#ra-side)" />
        <rect x="32" y="80" width="34" height="6" rx="3" fill="url(#ra-top)" />

        {/* extruded 3D bars */}
        <g>
          <rect x="25" y="50" width="9" height="16" fill="#7a1a06" opacity="0.45" />
          <rect x="23" y="48" width="9" height="16" fill="url(#ra-accent)" />
          <rect x="39" y="38" width="9" height="28" fill="#7a1a06" opacity="0.45" />
          <rect x="37" y="36" width="9" height="28" fill="url(#ra-accent)" />
          <rect x="53" y="44" width="9" height="22" fill="#7a1a06" opacity="0.45" />
          <rect x="51" y="42" width="9" height="22" fill="url(#ra-accent)" />
          <rect x="67" y="52" width="8" height="14" fill="#7a1a06" opacity="0.45" />
          <rect x="65" y="50" width="8" height="14" fill="url(#ra-accent)" />
        </g>
        <rect x="37" y="36" width="9" height="6" fill="#ffffff" opacity="0.25" />

        {/* live gauge, extruded disc */}
        <circle cx="93" cy="26" r="15" fill="url(#ra-side)" />
        <circle cx="92" cy="24" r="15" fill="url(#ra-top)" />
        <circle cx="92" cy="24" r="9" fill="#1f1f1f" />
        <path d="M85 29 A9 9 0 1 1 99 29" fill="none" stroke="url(#ra-accent)" strokeWidth="3" strokeLinecap="round" />
        <line x1="92" y1="24" x2="97" y2="18" stroke="url(#ra-accent)" strokeWidth="2.6" strokeLinecap="round" />
        <circle cx="92" cy="24" r="2.4" fill="#ffffff" />
        <circle cx="88" cy="19" r="5" fill="url(#ra-gloss)" />
      </g>
    </svg>
  ),

  AutomatedCourseCreation: ({ className }: IconProps) => (
    <svg viewBox="0 0 120 120" className={className} xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id="cc-topL" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#9a9a9a" />
          <stop offset="1" stopColor="#5a5a5a" />
        </linearGradient>
        <linearGradient id="cc-topR" x1="1" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#8a8a8a" />
          <stop offset="1" stopColor="#4d4d4d" />
        </linearGradient>
        <linearGradient id="cc-side" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#3a3a3a" />
          <stop offset="1" stopColor="#1f1f1f" />
        </linearGradient>
        <linearGradient id="cc-accent" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#ff8a63" />
          <stop offset="1" stopColor="#d9300c" />
        </linearGradient>
        <radialGradient id="cc-gloss" cx="0.35" cy="0.3" r="0.75">
          <stop offset="0" stopColor="#ffffff" stopOpacity="0.85" />
          <stop offset="1" stopColor="#ffffff" stopOpacity="0" />
        </radialGradient>
        <filter id="cc-shadow" x="-30%" y="-30%" width="160%" height="160%">
          <feDropShadow dx="0" dy="4" stdDeviation="4" floodColor="#000" floodOpacity="0.28" />
        </filter>
      </defs>

      <g filter="url(#cc-shadow)">
        {/* extruded open book, spine given thickness */}
        <path d="M60 46 C42 38 25 41 21 48 L21 92 C25 85 42 82 60 90 Z" fill="url(#cc-side)" transform="translate(0,3)" />
        <path d="M60 46 C78 38 95 41 99 48 L99 92 C95 85 78 82 60 90 Z" fill="url(#cc-side)" transform="translate(0,3)" />
        <path d="M60 43 C42 35 25 38 21 45 L21 89 C25 82 42 79 60 87 Z" fill="url(#cc-topL)" />
        <path d="M60 43 C78 35 95 38 99 45 L99 89 C95 82 78 79 60 87 Z" fill="url(#cc-topR)" />
        <rect x="57" y="43" width="6" height="47" fill="#2b2b2b" opacity="0.5" />

        <g stroke="#e6e6e6" strokeWidth="2.2" strokeLinecap="round" opacity="0.55">
          <line x1="30" y1="53" x2="52" y2="59" />
          <line x1="30" y1="63" x2="52" y2="69" />
          <line x1="30" y1="73" x2="46" y2="78" />
          <line x1="68" y1="59" x2="90" y2="53" />
          <line x1="68" y1="69" x2="90" y2="63" />
          <line x1="74" y1="78" x2="90" y2="73" />
        </g>

        {/* extruded gear/settings accent */}
        <circle cx="90" cy="27" r="12" fill="#7a1a06" opacity="0.4" transform="translate(0,3)" />
        <circle cx="90" cy="24" r="12" fill="url(#cc-accent)" />
        <circle cx="90" cy="24" r="5" fill="#d9300c" />
        <g stroke="url(#cc-accent)" strokeWidth="3.4" strokeLinecap="round">
          <line x1="90" y1="10" x2="90" y2="14" />
          <line x1="90" y1="34" x2="90" y2="38" />
          <line x1="76" y1="24" x2="80" y2="24" />
          <line x1="100" y1="24" x2="104" y2="24" />
          <line x1="80.5" y1="14.5" x2="83" y2="17" />
          <line x1="97" y1="31" x2="99.5" y2="33.5" />
          <line x1="80.5" y1="33.5" x2="83" y2="31" />
          <line x1="97" y1="17" x2="99.5" y2="14.5" />
        </g>
        <circle cx="86" cy="20" r="4" fill="url(#cc-gloss)" />
      </g>
    </svg>
  ),

  AutomatedQuizGeneration: ({ className }: IconProps) => (
    <svg viewBox="0 0 120 120" className={className} xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id="qz-top" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#9a9a9a" />
          <stop offset="1" stopColor="#4d4d4d" />
        </linearGradient>
        <linearGradient id="qz-side" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#3a3a3a" />
          <stop offset="1" stopColor="#1f1f1f" />
        </linearGradient>
        <radialGradient id="qz-orb" cx="0.35" cy="0.3" r="0.8">
          <stop offset="0" stopColor="#ff9a75" />
          <stop offset="0.55" stopColor="#f5401f" />
          <stop offset="1" stopColor="#b8280a" />
        </radialGradient>
        <radialGradient id="qz-gloss" cx="0.34" cy="0.26" r="0.5">
          <stop offset="0" stopColor="#ffffff" stopOpacity="0.9" />
          <stop offset="1" stopColor="#ffffff" stopOpacity="0" />
        </radialGradient>
        <filter id="qz-shadow" x="-30%" y="-30%" width="160%" height="160%">
          <feDropShadow dx="0" dy="4" stdDeviation="4" floodColor="#000" floodOpacity="0.28" />
        </filter>
      </defs>

      <g filter="url(#qz-shadow)">
        {/* extruded quiz card */}
        <rect x="24" y="22" width="52" height="72" rx="7" fill="url(#qz-side)" />
        <rect x="22" y="18" width="52" height="72" rx="7" fill="url(#qz-top)" />
        <rect x="22" y="18" width="52" height="18" rx="7" fill="#ffffff" opacity="0.08" />
        <rect x="34" y="12" width="16" height="9" rx="3" fill="url(#qz-side)" />

        <g stroke="#e6e6e6" strokeWidth="2.6" strokeLinecap="round" opacity="0.55">
          <line x1="30" y1="46" x2="60" y2="46" />
          <line x1="30" y1="56" x2="60" y2="56" />
          <line x1="30" y1="66" x2="52" y2="66" />
          <line x1="30" y1="76" x2="56" y2="76" />
        </g>
        <g fill="url(#qz-side)">
          <circle cx="30" cy="46" r="0" />
        </g>

        {/* glossy 3D orb with question mark */}
        <circle cx="83" cy="30" r="17" fill="#6b1607" opacity="0.4" transform="translate(0,4)" />
        <circle cx="83" cy="27" r="17" fill="url(#qz-orb)" />
        <ellipse cx="77" cy="20" rx="7" ry="5" fill="url(#qz-gloss)" />
        <text x="83" y="34" fontFamily="Arial, sans-serif" fontSize="20" fontWeight={800} fill="#ffffff" textAnchor="middle">
          ?
        </text>
      </g>
    </svg>
  ),

  AutomatedAssessments: ({ className }: IconProps) => (
    <svg viewBox="0 0 120 120" className={className} xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id="as-top" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#9a9a9a" />
          <stop offset="1" stopColor="#4d4d4d" />
        </linearGradient>
        <linearGradient id="as-side" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#3a3a3a" />
          <stop offset="1" stopColor="#1f1f1f" />
        </linearGradient>
        <linearGradient id="as-accent" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#ff8a63" />
          <stop offset="1" stopColor="#d9300c" />
        </linearGradient>
        <radialGradient id="as-gloss" cx="0.35" cy="0.28" r="0.75">
          <stop offset="0" stopColor="#ffffff" stopOpacity="0.85" />
          <stop offset="1" stopColor="#ffffff" stopOpacity="0" />
        </radialGradient>
        <filter id="as-shadow" x="-30%" y="-30%" width="160%" height="160%">
          <feDropShadow dx="0" dy="4" stdDeviation="4" floodColor="#000" floodOpacity="0.28" />
        </filter>
      </defs>

      <g filter="url(#as-shadow)">
        {/* extruded clipboard */}
        <rect x="22" y="24" width="52" height="70" rx="7" fill="url(#as-side)" />
        <rect x="20" y="20" width="52" height="70" rx="7" fill="url(#as-top)" />
        <rect x="20" y="20" width="52" height="18" rx="7" fill="#ffffff" opacity="0.08" />
        <rect x="35" y="13" width="18" height="10" rx="3" fill="url(#as-side)" />

        {/* extruded mini checkbox cubes */}
        <g>
          <rect x="28" y="46" width="9" height="9" rx="2" fill="url(#as-side)" />
          <rect x="27" y="45" width="9" height="9" rx="2" fill="url(#as-accent)" />
          <rect x="28" y="60" width="9" height="9" rx="2" fill="url(#as-side)" />
          <rect x="27" y="59" width="9" height="9" rx="2" fill="url(#as-accent)" />
          <rect x="28" y="74" width="9" height="9" rx="2" fill="url(#as-side)" />
          <rect x="27" y="73" width="9" height="9" rx="2" fill="url(#as-accent)" />
        </g>
        <g stroke="#e6e6e6" strokeWidth="2.6" strokeLinecap="round" opacity="0.55">
          <line x1="42" y1="49" x2="62" y2="49" />
          <line x1="42" y1="63" x2="62" y2="63" />
          <line x1="42" y1="77" x2="58" y2="77" />
        </g>

        {/* extruded glossy approval seal */}
        <circle cx="83" cy="80" r="16" fill="#7a1a06" opacity="0.4" transform="translate(0,3)" />
        <circle cx="83" cy="77" r="16" fill="url(#as-accent)" />
        <ellipse cx="77" cy="70" rx="6" ry="4.5" fill="#ffffff" opacity="0.3" />
        <polyline
          points="75,77 80,83 92,68"
          fill="none"
          stroke="#ffffff"
          strokeWidth="4"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </g>
    </svg>
  ),
};

export default Icons;
